import { Badge, Box, Group, Text, UnstyledButton } from "@mantine/core";
import { IconArrowRight, IconLink } from "@tabler/icons-react";
import { SaltFilter, SaltInfoData } from "api/product-search";

interface SaltInfoResultsProps {
  saltFilters: (SaltFilter | SaltInfoData)[];
  searchTerm: string;
  onSaltClick: (saltId: string, saltName: string) => void;
}

export const SaltInfoResults = ({
  saltFilters,
  searchTerm,
  onSaltClick,
}: SaltInfoResultsProps) => {
  if (!saltFilters || saltFilters.length === 0) {
    return null;
  }

  const highlightSearchTerm = (text: string, term: string) => {
    if (!term) return text;

    // Trim the search term to remove leading/trailing spaces
    const trimmedTerm = term.trim();
    if (!trimmedTerm) return text;

    const lowerText = text.toLowerCase();
    const lowerSearch = trimmedTerm.toLowerCase();

    // First try exact substring match (simple case)
    if (lowerText.includes(lowerSearch)) {
      const escapedTerm = trimmedTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(${escapedTerm})`, "gi");
      const parts = text.split(regex);

      return parts.map((part, index) => {
        const matches = part.toLowerCase() === lowerSearch;
        return matches ? (
          <Text
            key={index}
            component="span"
            style={{
              backgroundColor: "#FEF3C7",
              color: "#D97706",
              padding: "2px 4px",
              borderRadius: "4px",
              fontWeight: 600,
            }}
          >
            {part}
          </Text>
        ) : (
          part
        );
      });
    }

    // For partial matches (e.g., "amoxycillin + clavulanic acid" matching "amoxycillin 400mg/5ml + clavulanic acid 57mg/5ml")
    // Split search term by spaces and highlight each matching part
    const searchParts = lowerSearch
      .split(/\s+/)
      .filter((part) => part.trim().length > 0);
    if (searchParts.length === 0) return text;

    // Build an array of highlighted segments
    const segments: Array<{ text: string; highlight: boolean }> = [];
    let lastIndex = 0;
    let hasAnyMatch = false;

    for (const searchPart of searchParts) {
      const trimmedPart = searchPart.trim();
      if (trimmedPart.length === 0) continue;

      // Find the part in the remaining text (case-insensitive)
      // This allows "cl" to match "clavulanic" (partial word match)
      const remainingText = lowerText.substring(lastIndex);
      const partIndex = remainingText.indexOf(trimmedPart);

      if (partIndex !== -1) {
        hasAnyMatch = true;
        const actualIndex = lastIndex + partIndex;

        // Add text before the match (preserve original case)
        if (actualIndex > lastIndex) {
          segments.push({
            text: text.substring(lastIndex, actualIndex),
            highlight: false,
          });
        }

        // Add the highlighted match (preserve original case)
        // Use the actual length from the original text to preserve casing
        const matchLength = trimmedPart.length;
        segments.push({
          text: text.substring(actualIndex, actualIndex + matchLength),
          highlight: true,
        });

        lastIndex = actualIndex + matchLength;
      }
    }

    // Add remaining text
    if (lastIndex < text.length) {
      segments.push({
        text: text.substring(lastIndex),
        highlight: false,
      });
    }

    // If no matches found, return original text
    if (
      !hasAnyMatch ||
      segments.length === 0 ||
      segments.every((s) => !s.highlight)
    ) {
      return text;
    }

    // Merge adjacent highlighted segments for better visual appearance
    const mergedSegments: Array<{ text: string; highlight: boolean }> = [];
    for (let i = 0; i < segments.length; i++) {
      const current = segments[i];
      if (current.highlight && mergedSegments.length > 0) {
        const last = mergedSegments[mergedSegments.length - 1];
        if (last.highlight) {
          // Merge with previous highlighted segment
          last.text += current.text;
          continue;
        }
      }
      mergedSegments.push({ ...current });
    }

    // Render segments with highlighting
    return mergedSegments.map((segment, index) => {
      if (segment.highlight) {
        return (
          <Text
            key={index}
            component="span"
            style={{
              backgroundColor: "#FEF3C7",
              color: "#D97706",
              padding: "2px 4px",
              borderRadius: "4px",
              fontWeight: 600,
            }}
          >
            {segment.text}
          </Text>
        );
      }
      return <span key={index}>{segment.text}</span>;
    });
  };

  return (
    <Box>
      <Group position="apart" mb="xs" align="center">
        <Group spacing="xs" align="center">
          <IconLink size={15} color="#3B82F6" />
          <Text fw={500} size="lg" color="#3B82F6" style={{ fontSize: "11px" }}>
            Drug Salts & Compositions
          </Text>
        </Group>
        <Badge
          size="sm"
          variant="light"
          color="blue"
          style={{
            backgroundColor: "#DBEAFE",
            color: "#1D4ED8",
            fontSize: "11px",
            fontWeight: 500,
          }}
        >
          {saltFilters.length} found
        </Badge>
      </Group>

      {saltFilters.map((salt) => {
        // Handle both SaltFilter and SaltInfoData types
        const saltId = "code" in salt ? salt.code : salt.id;
        const saltName = salt.name;
        const totalCount = "totalCount" in salt ? salt.totalCount : undefined;

        return (
          <Box
            key={saltId}
            style={{
              borderBottom: "1px solid #F3F4F6",
              padding: "4px 0 8px 0",
              cursor: "pointer",
            }}
            onClick={() => onSaltClick(saltId, saltName)}
          >
            <Group position="apart" align="flex-start">
              <Box style={{ flex: 1 }}>
                <Text
                  fw={500}
                  size="md"
                  color="#1F2937"
                  mb="xs"
                  style={{ fontSize: "12px" }}
                >
                  {highlightSearchTerm(saltName, searchTerm)}
                </Text>

                {totalCount && (
                  <Text size="xs" color="#9CA3AF" style={{ fontSize: "10px" }}>
                    {totalCount} products available
                  </Text>
                )}
              </Box>

              <UnstyledButton
                style={{
                  color: "#DC2626",
                  fontSize: "11px",
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                View Products
                <IconArrowRight size={16} />
              </UnstyledButton>
            </Group>
          </Box>
        );
      })}
    </Box>
  );
};
